import { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { 
  HelpCircle, 
  Search, 
  Eye, 
  Bell, 
  Map, 
  FileText, 
  Settings, 
  CreditCard,
  ChevronDown,
  ChevronRight,
  Building,
  Database,
  MessageSquare,
  Shield,
  Mail,
  Download,
  ExternalLink,
  CheckCircle
} from 'lucide-react';

const HelpPage = () => {
  const [expandedSection, setExpandedSection] = useState('getting-started');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: HelpCircle,
      color: 'text-indigo-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Welcome to <span className="text-white font-semibold">Insite360</span> - your comprehensive building compliance monitoring platform. 
            This guide will help you get started with all the features available to you.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Quick Start Steps:</h4>
            <ol className="space-y-2 text-zinc-300">
              <li className="flex items-start gap-2">
                <span className="bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mt-0.5">1</span>
                <span><strong className="text-white">Create an Account</strong> - Sign up with your email and choose a subscription plan</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mt-0.5">2</span>
                <span><strong className="text-white">Add Properties</strong> - Search for addresses and add them to your watchlist</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mt-0.5">3</span>
                <span><strong className="text-white">Select Datasets</strong> - Choose which violation types to monitor (DOB, Fire, ECB, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mt-0.5">4</span>
                <span><strong className="text-white">Set Up Alerts</strong> - Configure email notifications for new violations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mt-0.5">5</span>
                <span><strong className="text-white">Monitor Dashboard</strong> - View your properties and violations at a glance</span>
              </li>
            </ol>
          </div>
        </div>
      )
    },
    {
      id: 'compliance-search',
      title: 'Compliance Search',
      icon: Search,
      color: 'text-emerald-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            The Compliance page allows you to search for violations across multiple NYC datasets by address, block, or lot.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">How to Search:</h4>
            <ol className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-1" />
                <span>Navigate to the <strong className="text-white">Compliance</strong> page from the sidebar</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-1" />
                <span>Select a <strong className="text-white">Dataset</strong> from the dropdown (DOB Violations, ECB, Fire, etc.)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-1" />
                <span>Enter an <strong className="text-white">Address</strong> - start typing and select from suggestions</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-1" />
                <span>Optionally filter by <strong className="text-white">Block</strong> and <strong className="text-white">Lot</strong> numbers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-1" />
                <span>Click <strong className="text-white">Search</strong> to view results</span>
              </li>
            </ol>
          </div>

          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
            <h4 className="text-indigo-400 font-medium mb-2">💡 Pro Tip</h4>
            <p className="text-zinc-300 text-sm">
              Use the "Add to Watchlist" button on any search result to quickly add a property to your monitoring list.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'watchlist',
      title: 'Managing Watchlists',
      icon: Eye,
      color: 'text-amber-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Watchlists allow you to group properties and monitor them for violations across multiple datasets.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Creating a Watchlist:</h4>
            <ol className="space-y-2 text-zinc-300">
              <li>1. Go to the <strong className="text-white">Watchlist</strong> page</li>
              <li>2. Click <strong className="text-white">Create New Watchlist</strong></li>
              <li>3. Enter a name (e.g., "Portfolio A" or "123 Main St Building")</li>
              <li>4. Search for an address to add your first property</li>
              <li>5. Select which datasets to monitor for that property</li>
            </ol>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Adding Properties:</h4>
            <ol className="space-y-2 text-zinc-300">
              <li>1. Click <strong className="text-white">Add Property</strong> on any watchlist</li>
              <li>2. Search for an address using the live search</li>
              <li>3. The Block/Lot will auto-fill from NYC data</li>
              <li>4. Check the datasets you want to monitor:</li>
            </ol>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="w-2 h-2 rounded-full bg-red-400"></span> DOB Violations
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="w-2 h-2 rounded-full bg-orange-400"></span> Fire Violations
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="w-2 h-2 rounded-full bg-yellow-400"></span> ECB Violations
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span> DOT Violations
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="w-2 h-2 rounded-full bg-green-400"></span> Permits
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <span className="w-2 h-2 rounded-full bg-purple-400"></span> DOB NOW
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Viewing Violations:</h4>
            <p className="text-zinc-300 text-sm">
              Click <strong className="text-white">View Violations</strong> on any watchlist to see all violations for properties in that list. 
              Results show violation ID, type, description, date, and current status.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'property-map',
      title: 'Property Map',
      icon: Map,
      color: 'text-blue-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            The interactive map displays all your watchlist properties with color-coded pins based on violation counts.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Pin Color Legend:</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded-full bg-emerald-500"></span>
                <span className="text-zinc-300"><strong className="text-white">Green</strong> - 0-2 violations (Low risk)</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded-full bg-amber-500"></span>
                <span className="text-zinc-300"><strong className="text-white">Amber</strong> - 3-10 violations (Medium risk)</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded-full bg-red-500"></span>
                <span className="text-zinc-300"><strong className="text-white">Red</strong> - 11+ violations (High risk)</span>
              </div>
            </div>
          </div>

          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
            <h4 className="text-indigo-400 font-medium mb-2">💡 Using the Map</h4>
            <ul className="text-zinc-300 text-sm space-y-1">
              <li>• Click any pin to see property details and violation count</li>
              <li>• Use mouse scroll to zoom in/out</li>
              <li>• Click and drag to pan around the map</li>
              <li>• Properties are automatically geocoded from NYC data</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'datasets',
      title: 'Understanding Datasets',
      icon: Database,
      color: 'text-purple-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Insite360 pulls data from multiple NYC Open Data sources. Here's what each dataset contains:
          </p>
          
          <div className="space-y-3">
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-red-400 font-medium">DOB Violations</h4>
              <p className="text-zinc-400 text-sm mt-1">
                Department of Buildings violations including illegal construction, unsafe conditions, and code violations.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-orange-400 font-medium">Fire Violations (FDNY)</h4>
              <p className="text-zinc-400 text-sm mt-1">
                Fire safety violations from FDNY inspections including sprinkler issues, blocked exits, and fire alarm problems.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-yellow-400 font-medium">ECB Violations</h4>
              <p className="text-zinc-400 text-sm mt-1">
                Environmental Control Board violations with associated fines and penalties.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-blue-400 font-medium">DOT Violations</h4>
              <p className="text-zinc-400 text-sm mt-1">
                Department of Transportation violations related to sidewalks, street furniture, and public right-of-way.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-green-400 font-medium">Permits</h4>
              <p className="text-zinc-400 text-sm mt-1">
                Building permit applications including new construction, alterations, and demolitions.
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4">
              <h4 className="text-purple-400 font-medium">DOB NOW</h4>
              <p className="text-zinc-400 text-sm mt-1">
                Applications filed through the DOB NOW system including job filings, inspections, and certificates.
              </p>
            </div>
          </div>

          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
            <h4 className="text-emerald-400 font-medium mb-2">📊 Data Freshness</h4>
            <p className="text-zinc-300 text-sm">
              All data is fetched live from NYC Open Data APIs. Your admin can configure sync frequency from hourly to weekly in the Admin Panel.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'flood-zones',
      title: 'Flood Zone Assessment',
      icon: Building,
      color: 'text-sky-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Each property includes comprehensive flood risk analysis from multiple NYC datasets.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Flood Zone Data Sources:</h4>
            <div className="space-y-3">
              <div className="p-3 bg-red-500/10 rounded-lg">
                <h5 className="text-red-400 font-medium text-sm">Hurricane Evacuation Zones</h5>
                <p className="text-xs text-zinc-400 mt-1">
                  <strong>Zone 1</strong> (highest risk) through <strong>Zone 6</strong> (lowest risk). Zones 1-2 require mandatory evacuation during hurricanes.
                </p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <h5 className="text-orange-400 font-medium text-sm">Sandy Inundation Zone (2012)</h5>
                <p className="text-xs text-zinc-400 mt-1">
                  Properties that were flooded during Hurricane Sandy. Important for insurance and flood mitigation planning.
                </p>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <h5 className="text-amber-400 font-medium text-sm">2050s Future Floodplain</h5>
                <p className="text-xs text-zinc-400 mt-1">
                  Climate projection model showing areas at risk of flooding by the 2050s due to sea level rise.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Viewing Flood Data:</h4>
            <ol className="space-y-2 text-zinc-300">
              <li>1. Click on any property from your Dashboard</li>
              <li>2. Look for the <strong className="text-sky-400">Flood Zone</strong> section in the property modal</li>
              <li>3. Color-coded badges show risk level:</li>
              <li className="pl-4 text-sm">
                <span className="inline-block w-2 h-2 bg-red-500 rounded mr-2"></span>
                <span className="text-red-400">Zone 1-2</span> - High risk, mandatory evacuation
              </li>
              <li className="pl-4 text-sm">
                <span className="inline-block w-2 h-2 bg-amber-500 rounded mr-2"></span>
                <span className="text-amber-400">Zone 3-4</span> - Moderate risk
              </li>
              <li className="pl-4 text-sm">
                <span className="inline-block w-2 h-2 bg-green-500 rounded mr-2"></span>
                <span className="text-green-400">Zone 5-6 / N/A</span> - Low risk
              </li>
            </ol>
          </div>

          <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-4">
            <h4 className="text-sky-400 font-medium mb-2">🌊 Why It Matters</h4>
            <p className="text-zinc-300 text-sm">
              Flood zone data impacts insurance requirements, emergency planning, and property values. Properties in high-risk zones may require flood insurance and should have evacuation plans.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'alerts',
      title: 'Alerts & Notifications',
      icon: Bell,
      color: 'text-rose-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Set up alerts to receive email notifications when new violations are detected on your monitored properties.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Setting Up Alerts:</h4>
            <ol className="space-y-2 text-zinc-300">
              <li>1. Navigate to the <strong className="text-white">Alerts</strong> page</li>
              <li>2. Click <strong className="text-white">Create Alert</strong></li>
              <li>3. Select a watchlist or specific property</li>
              <li>4. Choose which violation types to alert on</li>
              <li>5. Set your notification frequency (immediate, daily digest, weekly)</li>
              <li>6. Enter the email address for notifications</li>
            </ol>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <h4 className="text-amber-400 font-medium mb-2">⚠️ Note</h4>
            <p className="text-zinc-300 text-sm">
              Email notifications require your organization to have SendGrid configured. Contact your admin if you're not receiving alerts.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'ai-assistant',
      title: 'Help',
      icon: MessageSquare,
      color: 'text-cyan-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            The Help feature can help you understand violations, explain compliance requirements, and answer questions about NYC building codes.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Example Questions:</h4>
            <ul className="space-y-2 text-zinc-300">
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                "What does ECB violation code 1234 mean?"
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                "How do I resolve a DOB violation for illegal occupancy?"
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                "What are the deadlines for curing a fire safety violation?"
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                "Summarize the violations on 375 Park Avenue"
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400">→</span>
                "What permits are required for a commercial renovation?"
              </li>
            </ul>
          </div>

          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
            <h4 className="text-indigo-400 font-medium mb-2">💡 Pro Tip</h4>
            <p className="text-zinc-300 text-sm">
              Be specific in your questions. Include property addresses, violation numbers, or specific scenarios for the best responses.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: FileText,
      color: 'text-teal-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Generate custom reports for your properties to share with stakeholders, compliance teams, or management.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Report Types:</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-teal-500/10 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
                  <Download className="w-4 h-4 text-teal-400" />
                </div>
                <div>
                  <span className="text-teal-400 font-medium">Executive Report</span>
                  <p className="text-xs text-zinc-400 mt-1">Concise summary with property overview, violation counts, AI-powered strategic analysis (AI Insite), and property maps. Ideal for quick assessments and stakeholder briefings.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Download className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <span className="text-purple-400 font-medium">Extended Report</span>
                  <p className="text-xs text-zinc-400 mt-1">Complete multi-page PDF with all violations, permits, ECB data, electrical applications, flood zone info, maps, and AI Insite analysis. Best for detailed compliance review.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Generating a Report:</h4>
            <ol className="space-y-2 text-zinc-300">
              <li>1. Search for a property on the <strong className="text-white">Compliance</strong> page or open one from your <strong className="text-white">Dashboard</strong></li>
              <li>2. In the property details panel, find the <strong className="text-white">Report Buttons</strong></li>
              <li>3. Choose your report type:</li>
              <li className="pl-4 text-sm text-zinc-400">- <strong className="text-teal-400">Executive</strong> — Summary + AI Insite + maps</li>
              <li className="pl-4 text-sm text-zinc-400">- <strong className="text-purple-400">Extended Report</strong> — Executive + individual open violations & applications detail</li>
              <li>4. Wait for generation (loading spinner will appear)</li>
              <li>5. PDF downloads automatically</li>
            </ol>
          </div>

          <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
            <h4 className="text-indigo-400 font-medium mb-2">Pro Tip</h4>
            <p className="text-zinc-300 text-sm">
              Use the <strong>Executive</strong> report for quick stakeholder briefings. Use the <strong>Extended Report</strong> when you need complete violation-by-violation data for compliance reviews or legal purposes.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'subscription',
      title: 'Subscription & Billing',
      icon: CreditCard,
      color: 'text-green-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Manage your subscription plan and billing information from the Billing page.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Available Plans:</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded">
                <div>
                  <span className="text-white font-medium">Individual</span>
                  <p className="text-xs text-zinc-500">Up to 10 properties, 2 datasets</p>
                </div>
                <span className="text-emerald-400">$29/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded">
                <div>
                  <span className="text-white font-medium">Pro</span>
                  <p className="text-xs text-zinc-500">Up to 50 properties, 4 datasets</p>
                </div>
                <span className="text-emerald-400">$99/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-indigo-500/20 rounded border border-indigo-500/30">
                <div>
                  <span className="text-white font-medium">Enterprise</span>
                  <p className="text-xs text-zinc-500">Unlimited properties, all datasets</p>
                </div>
                <span className="text-emerald-400">$299/mo</span>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Billing Terms:</h4>
            <ul className="space-y-1 text-zinc-300 text-sm">
              <li>• Monthly, Quarterly (10% off), or Yearly (20% off) billing</li>
              <li>• 1, 2, or 3 year commitment options for additional savings</li>
              <li>• Cancel anytime - pro-rated refunds available</li>
              <li>• Secure payments via Stripe</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'account',
      title: 'Account Settings',
      icon: Settings,
      color: 'text-zinc-400',
      content: (
        <div className="space-y-4">
          <p className="text-zinc-300">
            Manage your account preferences, security settings, and profile information.
          </p>
          
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Available Settings:</h4>
            <ul className="space-y-2 text-zinc-300">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-zinc-500" />
                <span>Update email address</span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-zinc-500" />
                <span>Change password</span>
              </li>
              <li className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-zinc-500" />
                <span>Notification preferences</span>
              </li>
              <li className="flex items-center gap-2">
                <Download className="w-4 h-4 text-zinc-500" />
                <span>Export your data</span>
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Guide</h1>
            <p className="text-zinc-400 mt-1">Learn how to use Insite360 effectively</p>
          </div>
          <a
            href="/Insite360_User_Guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg transition-all text-sm font-medium"
            data-testid="download-user-guide"
          >
            <Download className="w-4 h-4" />
            Download User Guide (PDF)
          </a>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <a href="#getting-started" className="glass-card rounded-xl p-4 hover:bg-white/10 transition-colors">
            <HelpCircle className="w-8 h-8 text-indigo-400 mb-2" />
            <h3 className="text-white font-medium">Getting Started</h3>
            <p className="text-xs text-zinc-500 mt-1">New user guide</p>
          </a>
          <a href="#watchlist" className="glass-card rounded-xl p-4 hover:bg-white/10 transition-colors">
            <Eye className="w-8 h-8 text-amber-400 mb-2" />
            <h3 className="text-white font-medium">Watchlists</h3>
            <p className="text-xs text-zinc-500 mt-1">Monitor properties</p>
          </a>
          <a href="#reports" className="glass-card rounded-xl p-4 hover:bg-white/10 transition-colors">
            <FileText className="w-8 h-8 text-teal-400 mb-2" />
            <h3 className="text-white font-medium">Reports</h3>
            <p className="text-xs text-zinc-500 mt-1">Generate PDF & HTML</p>
          </a>
          <a href="#flood-zones" className="glass-card rounded-xl p-4 hover:bg-white/10 transition-colors">
            <Building className="w-8 h-8 text-sky-400 mb-2" />
            <h3 className="text-white font-medium">Flood Zones</h3>
            <p className="text-xs text-zinc-500 mt-1">Risk assessment</p>
          </a>
          <a href="#ai-assistant" className="glass-card rounded-xl p-4 hover:bg-white/10 transition-colors">
            <MessageSquare className="w-8 h-8 text-cyan-400 mb-2" />
            <h3 className="text-white font-medium">Help</h3>
            <p className="text-xs text-zinc-500 mt-1">Get help</p>
          </a>
        </div>

        {/* Accordion Sections */}
        <div className="glass-card rounded-xl overflow-hidden divide-y divide-white/10">
          {sections.map((section) => (
            <div key={section.id} id={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-5 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <section.icon className={`w-5 h-5 ${section.color}`} />
                  <span className="text-white font-medium">{section.title}</span>
                </div>
                {expandedSection === section.id ? (
                  <ChevronDown className="w-5 h-5 text-zinc-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-zinc-500" />
                )}
              </button>
              
              {expandedSection === section.id && (
                <div className="px-5 pb-5 pt-2 border-t border-white/5 bg-white/[0.02]">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Support */}
        <div className="glass-card rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
              <Mail className="w-6 h-6 text-indigo-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium">Need More Help?</h3>
              <p className="text-zinc-400 text-sm mt-1">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <a 
                  href="mailto:support@insite360.com" 
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                >
                  <Mail className="w-4 h-4" />
                  Contact Support
                </a>
                <a 
                  href="https://opendata.cityofnewyork.us/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  NYC Open Data Portal
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Data Sources Footer */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="text-white font-medium mb-4">📊 Data Sources</h3>
          <p className="text-zinc-400 text-sm mb-4">
            Insite360 fetches live data from the following NYC Open Data APIs:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <a 
              href="https://data.cityofnewyork.us/resource/3h2n-5cm9.json"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3" />
              DOB Violations API
            </a>
            <a 
              href="https://data.cityofnewyork.us/resource/6bgk-3dad.json"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3" />
              ECB Violations API
            </a>
            <a 
              href="https://data.cityofnewyork.us/resource/avgm-ztsb.json"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3" />
              Fire Violations API
            </a>
            <a 
              href="https://data.cityofnewyork.us/resource/ic3t-wcy2.json"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3" />
              Permits API
            </a>
            <a 
              href="https://data.cityofnewyork.us/resource/w9ak-ipjd.json"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3" />
              DOB NOW API
            </a>
            <a 
              href="https://geosearch.planninglabs.nyc/v2/search"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3" />
              NYC GeoSearch API
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpPage;
